import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, Button, cn } from '@pantry/ui';
import {
  useItemInstances,
  type ItemWithInstanceCounts,
  UNIT_DISPLAY_LABELS,
} from '@pantry/api-client';
import {
  type Product,
  type Household,
  getDaysUntilExpiry,
  getExpiryStatus as getExpiryStatusShared,
  resolveExpiryThreshold,
} from '@pantry/shared';
import { getExpiryDisplayText } from '../../lib/expiry-display';
import { useMealPrepStore } from '@pantry/shared-react/stores';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { ConsumeReasonDialog } from './consume-reason-dialog';
import { WasteReasonDialog } from './waste-reason-dialog';
import { QuantityActionDialog } from './quantity-action-dialog';
import {
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/**
 * Item card component displaying item info with expiry status.
 * Clicking the card navigates to item detail page.
 * Actions: Mark consumed, Mark wasted (with reason dialog).
 */
export function ItemCard({
  item,
  householdId,
  household,
  compact = false,
}: {
  item: ItemWithInstanceCounts;
  householdId: string;
  household: Household | null;
  compact?: boolean;
}) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const { data: instances } = useItemInstances(householdId, item.id);
  const resolveProductName = useResolveProductName();
  const [showWasteDialog, setShowWasteDialog] = useState(false);
  const [wasteInstanceId, setWasteInstanceId] = useState<string | null>(null);
  const [showConsumeDialog, setShowConsumeDialog] = useState(false);
  const [consumeInstanceId, setConsumeInstanceId] = useState<string | null>(null);
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);
  const [quantityAction, setQuantityAction] = useState<'consume' | 'waste'>('consume');

  // Meal prep mode integration
  const { isActive: mealPrepActive, toggleSelection, isSelected } = useMealPrepStore();

  // Get the earliest expiring active instance
  const activeInstances = instances?.filter((i) => i.status === 'active') || [];
  // Sort active instances by expiry date (earliest first, null dates last)
  const sortedActiveInstances = [...activeInstances].sort((a, b) => {
    if (!a.expiryDate && !b.expiryDate) return 0;
    if (!a.expiryDate) return 1;
    if (!b.expiryDate) return -1;
    return a.expiryDate.localeCompare(b.expiryDate);
  });

  const expiringInstance = sortedActiveInstances.find((i) => i.expiryDate);

  // Use earliest expiring, or first active instance
  const targetInstance = expiringInstance || activeInstances[0];

  // Check if this item's target instance is selected for meal prep
  const isItemSelected = targetInstance ? isSelected(targetInstance.id) : false;

  const expiryDate = expiringInstance?.expiryDate;
  const daysUntilExpiry = expiryDate ? getDaysUntilExpiry(expiryDate) : null;

  const handleCardClick = () => {
    // In meal prep mode - toggle selection instead of navigating
    if (mealPrepActive && targetInstance) {
      toggleSelection(item.id, targetInstance.id);
      return;
    }
    navigate({ to: '/items/$itemId', params: { itemId: item.id } });
  };

  const handleMarkConsumed = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation
    if (!targetInstance) return;

    // If multiple active instances, show quantity dialog
    if (activeInstances.length > 1) {
      setQuantityAction('consume');
      setShowQuantityDialog(true);
      return;
    }

    // Single instance - show consume reason dialog
    setConsumeInstanceId(targetInstance.id);
    setShowConsumeDialog(true);
  };

  const handleMarkWasted = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation
    if (!targetInstance) return;

    // If multiple active instances, show quantity dialog
    if (activeInstances.length > 1) {
      setQuantityAction('waste');
      setShowQuantityDialog(true);
      return;
    }

    // Single instance - show waste reason dialog directly
    setWasteInstanceId(targetInstance.id);
    setShowWasteDialog(true);
  };

  // Display custom name if set, fall back to localized product name, then "Unnamed Item"
  const itemName =
    item.customName ||
    (item.product ? resolveProductName(item.product as Product) : null) ||
    t`Unnamed Item`;
  const itemBrand = item.product?.brand;
  const itemImage = item.product?.imageUrl;

  // Determine expiry status using resolved threshold
  const threshold = resolveExpiryThreshold(item, item.storageLocation ?? null, household);
  const expiryStatus = getExpiryStatusShared(daysUntilExpiry, threshold);

  // Compact mode for nested items in consolidated view
  if (compact) {
    return (
      <>
        <div
          onClick={handleCardClick}
          className={cn(
            'flex items-center gap-3 p-3 rounded-lg bg-muted/30 border cursor-pointer hover:bg-muted/50 transition-colors',
            expiryStatus === 'expired' && 'border-expired/30',
            expiryStatus === 'expiring-today' && 'border-expiring-today/30',
            expiryStatus === 'expiring-soon' && 'border-expiring-soon/30',
            !['expired', 'expiring-today', 'expiring-soon'].includes(expiryStatus) &&
              'border-border',
            // Meal prep selection styling
            isItemSelected && 'ring-2 ring-primary border-primary bg-primary/5'
          )}
        >
          {/* Checkbox indicator for meal prep mode */}
          {mealPrepActive && (
            <div
              className={cn(
                'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border',
                isItemSelected
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-muted-foreground'
              )}
            >
              {isItemSelected && <CheckIcon className="h-3 w-3" />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{itemName}</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">
                <Trans>
                  Qty: {item.activeQuantity ?? 0} {UNIT_DISPLAY_LABELS[item.unit]}
                </Trans>
              </span>
              {expiryStatus !== 'none' && expiryDate && (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs',
                    expiryStatus === 'expired' && 'bg-expired/20 text-expired',
                    expiryStatus === 'expiring-today' && 'bg-expiring-today/20 text-expiring-today',
                    expiryStatus === 'expiring-soon' && 'bg-expiring-soon/20 text-expiring-soon',
                    expiryStatus === 'fresh' && 'bg-muted text-muted-foreground'
                  )}
                >
                  {getExpiryDisplayText(daysUntilExpiry, expiryDate, t, i18n.locale, threshold)}
                </span>
              )}
              {item.notes && (
                <span className="text-xs text-muted-foreground truncate">{item.notes}</span>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-1">
            {targetInstance && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkConsumed}
                  title={t`Mark as consumed`}
                  className="h-7 w-7 p-0"
                >
                  <CheckIcon className="h-4 w-4 text-consumed" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkWasted}
                  title={t`Mark as wasted`}
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Consume reason dialog (single instance) */}
        {showConsumeDialog && consumeInstanceId && (
          <ConsumeReasonDialog
            householdId={householdId}
            itemId={item.id}
            instanceId={consumeInstanceId}
            instanceQuantity={instances?.find((i) => i.id === consumeInstanceId)?.quantity}
            unit={item.unit}
            onClose={() => {
              setShowConsumeDialog(false);
              setConsumeInstanceId(null);
            }}
          />
        )}

        {/* Waste reason dialog (single instance) */}
        {showWasteDialog && wasteInstanceId && (
          <WasteReasonDialog
            householdId={householdId}
            itemId={item.id}
            instanceId={wasteInstanceId}
            instanceQuantity={instances?.find((i) => i.id === wasteInstanceId)?.quantity}
            unit={item.unit}
            onClose={() => {
              setShowWasteDialog(false);
              setWasteInstanceId(null);
            }}
          />
        )}

        {/* Quantity action dialog (multiple instances) */}
        {showQuantityDialog && sortedActiveInstances.length > 0 && (
          <QuantityActionDialog
            householdId={householdId}
            itemId={item.id}
            instances={sortedActiveInstances}
            unit={item.unit}
            action={quantityAction}
            onClose={() => setShowQuantityDialog(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Card
        onClick={handleCardClick}
        className={cn(
          'transition-all duration-200 hover:shadow-md cursor-pointer group overflow-hidden',
          expiryStatus === 'expired' && 'border-expired/50 bg-expired/5',
          expiryStatus === 'expiring-today' && 'border-expiring-today/50',
          expiryStatus === 'expiring-soon' && 'border-expiring-soon/30',
          // Meal prep selection styling
          isItemSelected && 'ring-2 ring-primary border-primary bg-primary/5'
        )}
      >
        <CardContent className="p-4">
          <div className="flex gap-4">
            {/* Meal prep selection checkbox */}
            {mealPrepActive && (
              <div
                className={cn(
                  'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border self-center transition-colors',
                  isItemSelected
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'border-muted-foreground'
                )}
              >
                {isItemSelected && <CheckIcon className="h-4 w-4" />}
              </div>
            )}
            {/* Product image or status indicator */}
            <div
              className={cn(
                'relative flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl overflow-hidden transition-transform group-hover:scale-105',
                !itemImage && expiryStatus === 'expired' && 'bg-expired/10',
                !itemImage && expiryStatus === 'expiring-today' && 'bg-expiring-today/10',
                !itemImage && expiryStatus === 'expiring-soon' && 'bg-expiring-soon/10',
                !itemImage && (expiryStatus === 'fresh' || expiryStatus === 'none') && 'bg-muted'
              )}
            >
              {itemImage ? (
                <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
              ) : expiryStatus === 'expired' ||
                expiryStatus === 'expiring-today' ||
                expiryStatus === 'expiring-soon' ? (
                <ExclamationTriangleIcon
                  className={cn(
                    'h-7 w-7',
                    expiryStatus === 'expired' && 'text-expired',
                    expiryStatus === 'expiring-today' && 'text-expiring-today',
                    expiryStatus === 'expiring-soon' && 'text-expiring-soon'
                  )}
                />
              ) : (
                <ArchiveBoxIcon className="h-7 w-7 text-muted-foreground" />
              )}
              {/* Status indicator dot */}
              {expiryStatus !== 'none' && expiryStatus !== 'fresh' && (
                <div
                  className={cn(
                    'absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card',
                    expiryStatus === 'expired' && 'bg-expired',
                    expiryStatus === 'expiring-today' && 'bg-expiring-today',
                    expiryStatus === 'expiring-soon' && 'bg-expiring-soon'
                  )}
                />
              )}
            </div>

            {/* Item info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
                {itemName}
              </h3>
              {itemBrand && <p className="text-xs text-muted-foreground truncate">{itemBrand}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {/* Active Quantity */}
                <span className="inline-flex items-center rounded-md bg-muted/70 px-2 py-0.5 text-xs text-foreground font-medium">
                  {item.activeQuantity ?? 0} {UNIT_DISPLAY_LABELS[item.unit]}
                </span>

                {/* Expiry status with date */}
                {expiryStatus !== 'none' && expiryDate && (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                      expiryStatus === 'expired' && 'bg-expired/20 text-expired',
                      expiryStatus === 'expiring-today' &&
                        'bg-expiring-today/20 text-expiring-today',
                      expiryStatus === 'expiring-soon' && 'bg-expiring-soon/20 text-expiring-soon',
                      expiryStatus === 'fresh' && 'bg-fresh/10 text-fresh'
                    )}
                  >
                    {getExpiryDisplayText(daysUntilExpiry, expiryDate, t, i18n.locale, threshold)}
                  </span>
                )}

                {/* Active instances count */}
                {(item.activeInstanceCount ?? 0) > 1 && (
                  <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs text-primary font-medium">
                    <Trans>{item.activeInstanceCount} batches</Trans>
                  </span>
                )}
              </div>
              {item.notes && (
                <p className="mt-1.5 text-xs text-muted-foreground truncate italic">{item.notes}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-shrink-0 flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {targetInstance && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkConsumed}
                    title={t`Mark as consumed`}
                    className="h-8 w-8 p-0 hover:bg-fresh/10"
                  >
                    <CheckIcon className="h-4 w-4 text-fresh" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkWasted}
                    title={t`Mark as wasted`}
                    className="h-8 w-8 p-0 hover:bg-expired/10"
                  >
                    <XMarkIcon className="h-4 w-4 text-expired" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consume reason dialog (single instance) */}
      {showConsumeDialog && consumeInstanceId && (
        <ConsumeReasonDialog
          householdId={householdId}
          itemId={item.id}
          instanceId={consumeInstanceId}
          instanceQuantity={instances?.find((i) => i.id === consumeInstanceId)?.quantity}
          unit={item.unit}
          onClose={() => {
            setShowConsumeDialog(false);
            setConsumeInstanceId(null);
          }}
        />
      )}

      {/* Waste reason dialog (single instance) */}
      {showWasteDialog && wasteInstanceId && (
        <WasteReasonDialog
          householdId={householdId}
          itemId={item.id}
          instanceId={wasteInstanceId}
          instanceQuantity={instances?.find((i) => i.id === wasteInstanceId)?.quantity}
          unit={item.unit}
          onClose={() => {
            setShowWasteDialog(false);
            setWasteInstanceId(null);
          }}
        />
      )}

      {/* Quantity action dialog (multiple instances) */}
      {showQuantityDialog && sortedActiveInstances.length > 0 && (
        <QuantityActionDialog
          householdId={householdId}
          itemId={item.id}
          instances={sortedActiveInstances}
          unit={item.unit}
          action={quantityAction}
          onClose={() => setShowQuantityDialog(false)}
        />
      )}
    </>
  );
}
